import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './Reports.css';
import '../Shimmer/Shimmer.css';
import CustomDropdown from '../CustomDropdown';
import usePagination from '../../hooks/usePagination';
import { API_BASE_URL } from '../../utils/api';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { normalizeDate } from '../../utils/dates';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

function Reports({ selectedApplication, ws, isConnected, sendMessage, onRunTest, onSelectTest }) {
  const [timeFilter, setTimeFilter] = useState('Last 7 Days');
  const [loading, setLoading] = useState(true);
  const [, setChartLoading] = useState(false); // value was only read by the removed trends chart
  
  // Metrics state
  const [overviewMetrics, setOverviewMetrics] = useState([
    {
      title: 'Total Executions',
      value: '0',
      change: 'No data',
      icon: 'blue',
      trend: 'neutral'
    },
    {
      title: 'Pass Rate',
      value: '0%',
      change: 'No data',
      icon: 'green',
      trend: 'neutral'
    },
    {
      title: 'Avg Duration',
      value: '0s',
      change: 'No data',
      icon: 'purple',
      trend: 'neutral'
    }
  ]);

  // Chart data state
  const [chartData, setChartData] = useState({
    labels: [],
    passed: [],
    failed: []
  });

  // Pass/Fail distribution state
  const [passFailData, setPassFailData] = useState({
    passed: 0,
    failed: 0,
    passRate: 0
  });

  // Failing tests state
  const [failingTests, setFailingTests] = useState([]);
  
  // Pagination for failing tests
  const failingTestsPagination = usePagination(failingTests, 10);
  const [failingTestsLoading, setFailingTestsLoading] = useState(false);
  // The last run, with each case in it. This is the question a tester actually
  // opens the report to answer: did last night pass, and if not, what broke?
  const [latestRun, setLatestRun] = useState(null);
  const [latestCases, setLatestCases] = useState([]);
  const [latestLoading, setLatestLoading] = useState(false);
  const [suites, setSuites] = useState([]);
  const [suiteFilter, setSuiteFilter] = useState('All Suites');

  // Get days from time filter
  const getDaysFromFilter = (filter) => {
    switch (filter) {
      case 'Last 7 Days':
        return 7;
      case 'Last 30 Days':
        return 30;
      case 'Last 90 Days':
        return 90;
      case 'All Time':
        return 365; // Use 365 days as "all time"
      default:
        return 7;
    }
  };

  // Format time ago
  const getTimeAgo = (date) => {
    if (!date) return 'N/A';
    const now = new Date();
    const diff = now - new Date(date);
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days} day${days > 1 ? 's' : ''} ago`;
    if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    if (minutes > 0) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    return 'Just now';
  };

  // Fetch test run statistics
  // setSuites hands back a NEW array every time, even when the suites are
  // identical. Depending on that array made fetchLatestRun a new function on
  // every fetch, which re-ran the effect, which fetched again - an endless loop.
  // Depend on the selected suite's id instead: a string, stable by value.
  const selectedSuiteId = useMemo(
    () => suites.find((x) => x.name === suiteFilter)?.id ?? null,
    [suites, suiteFilter]
  );

  const fetchTestRunStats = useCallback(async () => {
    if (!selectedApplication) return;

    setLoading(true);
    const startTime = Date.now();
    const minLoadingTime = 1000;

    try {
      const days = getDaysFromFilter(timeFilter);
      const response = await fetch(
        `${API_BASE_URL}/api/statistics/test-runs?application_id=${selectedApplication.id}&days=${days}${selectedSuiteId ? `&test_suite_id=${selectedSuiteId}` : ''}`
      );
      const data = await response.json();

      if (data.success && data.stats) {
        const stats = data.stats;
        const totalRuns = stats.totalRuns || 0;
        const passed = stats.passed || 0;
        const failed = stats.failed || 0;
        const successRate = stats.successRate || 0;
        const avgDuration = stats.avgDuration || 0;

        // Calculate previous period for comparison
        const previousDays = days;
        const previousResponse = await fetch(
          `${API_BASE_URL}/api/statistics/test-runs?application_id=${selectedApplication.id}&days=${previousDays * 2}`
        );
        const previousData = await previousResponse.json();
        
        let previousTotalRuns = 0;
        let previousSuccessRate = 0;
        let previousAvgDuration = 0;
        
        if (previousData.success && previousData.stats) {
          // Calculate stats for the previous period (first half of the range)
          const allRunsResponse = await fetch(
            `${API_BASE_URL}/api/test-runs?limit=10000&application_id=${selectedApplication.id}`
          );
          const allRunsData = await allRunsResponse.json();
          
          if (allRunsData.success && allRunsData.testRuns) {
            const now = new Date();
            const currentPeriodStart = new Date(now);
            currentPeriodStart.setDate(currentPeriodStart.getDate() - days);
            const previousPeriodStart = new Date(currentPeriodStart);
            previousPeriodStart.setDate(previousPeriodStart.getDate() - days);
            
            const previousPeriodRuns = allRunsData.testRuns.filter(run => {
              if (!run.completed_at) return false;
              const runDate = normalizeDate(run.completed_at);
              return runDate >= previousPeriodStart && runDate < currentPeriodStart;
            });
            
            previousTotalRuns = previousPeriodRuns.length;
            const previousPassed = previousPeriodRuns.filter(r => r.status === 'passed').length;
            previousSuccessRate = previousTotalRuns > 0 ? (previousPassed / previousTotalRuns) * 100 : 0;
            
            const previousDurations = previousPeriodRuns
              .filter(r => r.duration)
              .map(r => r.duration / 1000);
            previousAvgDuration = previousDurations.length > 0
              ? previousDurations.reduce((a, b) => a + b, 0) / previousDurations.length
              : 0;
          }
        }

        // Calculate changes
        const totalChange = previousTotalRuns > 0
          ? `${((totalRuns - previousTotalRuns) / previousTotalRuns * 100).toFixed(1)}% from last period`
          : 'No previous data';
        const totalTrend = totalRuns > previousTotalRuns ? 'up' : totalRuns < previousTotalRuns ? 'down' : 'neutral';

        const passRateChange = previousSuccessRate > 0
          ? `${(successRate - previousSuccessRate).toFixed(1)}% ${successRate >= previousSuccessRate ? 'improvement' : 'decrease'}`
          : 'No previous data';
        const passRateTrend = successRate >= previousSuccessRate ? 'up' : 'down';

        const durationChange = previousAvgDuration > 0
          ? `${(avgDuration - previousAvgDuration).toFixed(1)}s ${avgDuration < previousAvgDuration ? 'faster' : 'slower'}`
          : 'No previous data';
        const durationTrend = avgDuration < previousAvgDuration ? 'down' : avgDuration > previousAvgDuration ? 'up' : 'neutral';

        setOverviewMetrics([
          {
            title: 'Total Executions',
            value: totalRuns.toString(),
            change: totalChange,
            icon: 'blue',
            trend: totalTrend
          },
          {
            title: 'Pass Rate',
            value: `${successRate.toFixed(1)}%`,
            change: passRateChange,
            icon: 'green',
            trend: passRateTrend
          },
          {
            title: 'Avg Duration',
            value: `${avgDuration.toFixed(1)}s`,
            change: durationChange,
            icon: 'purple',
            trend: durationTrend
          }
        ]);

        // Update pass/fail distribution
        setPassFailData({
          passed,
          failed,
          passRate: successRate
        });
      }
    } catch (error) {
      console.error('Error fetching test run statistics:', error);
    } finally {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, minLoadingTime - elapsed);
      setTimeout(() => {
        setLoading(false);
      }, remaining);
    }
  }, [selectedApplication, timeFilter, selectedSuiteId]);

  // Fetch chart data
  const fetchChartData = useCallback(async () => {
    if (!selectedApplication) return;

    setChartLoading(true);
    const startTime = Date.now();
    const minLoadingTime = 500;

    try {
      const days = getDaysFromFilter(timeFilter);
      const response = await fetch(
        `${API_BASE_URL}/api/statistics/test-runs-by-date?application_id=${selectedApplication.id}&days=${days}`
      );
      const data = await response.json();

      if (data.success && data.data) {
        setChartData(data.data);
      } else {
        setChartData({
          labels: [],
          passed: [],
          failed: []
        });
      }
    } catch (error) {
      console.error('Error fetching chart data:', error);
      setChartData({
        labels: [],
        passed: [],
        failed: []
      });
    } finally {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, minLoadingTime - elapsed);
      setTimeout(() => {
        setChartLoading(false);
      }, remaining);
    }
  }, [selectedApplication, timeFilter]);

  // Fetch failing tests
  const fetchSuites = useCallback(async () => {
    if (!selectedApplication) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/test-suites?application_id=${selectedApplication.id}`);
      const data = await response.json();
      setSuites(data.success && Array.isArray(data.testSuites) ? data.testSuites : []);
    } catch {
      setSuites([]);
    }
  }, [selectedApplication]);

  const fetchLatestRun = useCallback(async () => {
    if (!selectedApplication) return;
    setLatestLoading(true);
    try {
      const suiteParam = selectedSuiteId ? `&test_suite_id=${selectedSuiteId}` : '';
      const response = await fetch(
        `${API_BASE_URL}/api/statistics/latest-run?application_id=${selectedApplication.id}${suiteParam}`
      );
      const data = await response.json();
      setLatestRun(data.success ? data.run : null);
      setLatestCases(data.success && Array.isArray(data.cases) ? data.cases : []);
    } catch {
      setLatestRun(null);
      setLatestCases([]);
    } finally {
      setLatestLoading(false);
    }
  }, [selectedApplication, selectedSuiteId]);

  // Recent runs, grouped by suite.
  //
  // This replaced a "Top Failing Tests" table that grouped by test_case_id -
  // and every suite run stores test_case_id as NULL, so it skipped every row
  // this team actually produces and was permanently empty. Grouping by suite
  // works with the data that exists.
  const fetchFailingTests = useCallback(async () => {
    if (!selectedApplication) return;
    setFailingTestsLoading(true);

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/test-runs?limit=10000&application_id=${selectedApplication.id}`
      );
      const data = await response.json();

      if (!data.success || !Array.isArray(data.testRuns)) {
        setFailingTests([]);
        return;
      }

      const days = getDaysFromFilter(timeFilter);
      const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;

      const bySuite = {};
      data.testRuns.forEach((run) => {
        const finishedAt = normalizeDate(run.completed_at);
        if (!finishedAt || finishedAt.getTime() < cutoff) return;
        if (selectedSuiteId && String(run.test_suite_id) !== String(selectedSuiteId)) return;

        const key = run.test_suite_id || run.test_case_id || run.test_name;
        if (!bySuite[key]) {
          bySuite[key] = { key, name: run.test_suite_name || run.test_name || 'Unnamed', runs: [] };
        }
        bySuite[key].runs.push({ status: run.status, at: finishedAt });
      });

      const grouped = Object.values(bySuite)
        .map((group) => {
          const runs = group.runs.sort((a, b) => b.at - a.at);
          const passed = runs.filter((r) => r.status === 'passed').length;
          return {
            key: group.key,
            name: group.name,
            total: runs.length,
            passed,
            passRate: runs.length ? Math.round((passed / runs.length) * 100) : 0,
            lastRun: runs[0] ? getTimeAgo(runs[0].at.toISOString()) : 'Never',
            lastRunAt: runs[0] ? runs[0].at.getTime() : 0,
            // newest last, so the strip reads left-to-right like a timeline
            strip: runs.slice(0, 12).reverse().map((r) => r.status),
          };
        })
        .sort((a, b) => b.lastRunAt - a.lastRunAt); // newest activity first

      setFailingTests(grouped);
    } catch (error) {
      setFailingTests([]);
    } finally {
      setFailingTestsLoading(false);
    }
  }, [selectedApplication, timeFilter, selectedSuiteId]);

  // Fetch all data when component mounts or selectedApplication/timeFilter changes
  useEffect(() => {
    if (selectedApplication) {
      fetchTestRunStats();
      fetchChartData();
      fetchFailingTests();
      fetchLatestRun();
    } else {
      setOverviewMetrics([
        {
          title: 'Total Executions',
          value: '0',
          change: 'Select an application',
          icon: 'blue',
          trend: 'neutral'
        },
        {
          title: 'Pass Rate',
          value: '0%',
          change: 'Select an application',
          icon: 'green',
          trend: 'neutral'
        },
        {
          title: 'Avg Duration',
          value: '0s',
          change: 'Select an application',
          icon: 'purple',
          trend: 'neutral'
        }
      ]);
      setChartData({ labels: [], passed: [], failed: [] });
      setPassFailData({ passed: 0, failed: 0, passRate: 0 });
      setFailingTests([]);
      setLatestRun(null);
      setLatestCases([]);
      setLoading(false);
    }
  }, [selectedApplication, timeFilter, fetchTestRunStats, fetchChartData, fetchFailingTests, fetchLatestRun]);

  // Suites change only when the application does, so load them on their own.
  useEffect(() => {
    fetchSuites();
  }, [fetchSuites]);

  // Reset pagination when failing tests change
  useEffect(() => {
    failingTestsPagination.resetPage();
    // Deliberate: keyed on the count only. The pagination object is rebuilt
    // on every render, so listing it would reset the page constantly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [failingTests.length]);


  // Not wired up yet - kept for the failing-tests colour coding.
  // eslint-disable-next-line no-unused-vars
  const getColorForFailures = (failures) => {
    if (failures >= 10) return 'red';
    if (failures >= 5) return 'orange';
    return 'yellow';
  };

  // Handle View Details


  // Handle Rerun

  // Format duration helper

  // Get status badge helper

  // Handle Export Report
  const handleExportReport = () => {
    try {
      // Prepare report data
      const reportData = {
        generatedAt: new Date().toISOString(),
        timeFilter: timeFilter,
        application: selectedApplication ? selectedApplication.name : 'N/A',
        metrics: overviewMetrics,
        chartData: chartData,
        passFailData: passFailData,
        failingTests: failingTests
      };

      // Convert to CSV format
      let csvContent = 'Test Execution Report\n';
      csvContent += `Generated: ${new Date().toLocaleString()}\n`;
      csvContent += `Time Filter: ${timeFilter}\n`;
      csvContent += `Application: ${reportData.application}\n\n`;

      // Metrics section
      csvContent += 'Overview Metrics\n';
      csvContent += 'Metric,Value\n';
      overviewMetrics.forEach(metric => {
        csvContent += `"${metric.title}","${metric.value}"\n`;
      });
      csvContent += '\n';

      // Pass/Fail Distribution
      csvContent += 'Pass/Fail Distribution\n';
      csvContent += `Passed,${passFailData.passed}\n`;
      csvContent += `Failed,${passFailData.failed}\n`;
      csvContent += `Pass Rate,${passFailData.passRate.toFixed(1)}%\n\n`;

      // Chart data (if available)
      if (chartData.labels && chartData.labels.length > 0) {
        csvContent += 'Test Execution Trends\n';
        csvContent += 'Date,Passed,Failed\n';
        chartData.labels.forEach((label, index) => {
          csvContent += `"${label}",${chartData.passed[index] || 0},${chartData.failed[index] || 0}\n`;
        });
        csvContent += '\n';
      }

      // Failing tests
      if (failingTests.length > 0) {
        csvContent += 'Top Failing Tests\n';
        csvContent += 'Test Name,Suite,Failures,Success Rate,Last Failed\n';
        failingTests.forEach(test => {
          csvContent += `"${test.name}","${test.suite}",${test.failures},${test.successRate}%,"${test.lastFailed}"\n`;
        });
      }

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `test-report-${selectedApplication ? selectedApplication.name.replace(/[^a-zA-Z0-9]/g, '_') : 'report'}-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error exporting report:', error);
      alert('Error exporting report. Please try again.');
    }
  };

  return (
    <div className="reports-container">
      {/* Header Controls */}
      <div className="reports-header-controls">
        <CustomDropdown
          value={suiteFilter}
          onChange={(e) => setSuiteFilter(e.target.value)}
          options={[
            { value: 'All Suites', label: 'All Suites' },
            ...suites.map((suite) => ({ value: suite.name, label: suite.name })),
          ]}
          className="time-filter-select"
          style={{ width: '18%' }}
        />
        <CustomDropdown
          value={timeFilter}
          onChange={(e) => setTimeFilter(e.target.value)}
          options={[
            { value: 'Last 7 Days', label: 'Last 7 Days' },
            { value: 'Last 30 Days', label: 'Last 30 Days' },
            { value: 'Last 90 Days', label: 'Last 90 Days' },
            { value: 'All Time', label: 'All Time' }
          ]}
          className="time-filter-select"
          style={{ width: '15%' }}
        />
        <button 
          className="btn-export-report" 
          onClick={handleExportReport}
          disabled={!selectedApplication || loading}
          title="Export report as CSV"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ marginRight: '0.5rem', display: 'inline-block', verticalAlign: 'middle' }}>
            <path d="M8 10L4 6H6V2H10V6H12L8 10Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
            <path d="M2 13V14H14V13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Export Report
        </button>
      </div>

      {/* Latest run - the first thing anyone wants to know */}
      <div className="latest-run-card">
        {latestLoading ? (
          <p className="latest-run-empty">Loading the last run&hellip;</p>
        ) : !latestRun ? (
          <p className="latest-run-empty">No runs yet for this selection.</p>
        ) : (
          <>
            <div className="latest-run-header">
              <div>
                <span className="latest-run-label">Latest run</span>
                <h3 className="latest-run-name">{latestRun.name}</h3>
              </div>
              <span className={`latest-run-badge ${latestRun.status}`}>
                {latestRun.status === 'passed' ? 'Passed' : 'Failed'}
              </span>
            </div>

            <p className="latest-run-meta">
              {getTimeAgo(latestRun.completed_at)}
              {latestRun.duration ? ` \u00b7 ${(latestRun.duration / 1000).toFixed(1)}s` : ''}
              {latestCases.length ? ` \u00b7 ${latestCases.filter((c) => c.status === 'passed').length} of ${latestCases.length} cases passed` : ''}
            </p>

            {latestCases.length > 0 && (
              <ul className="latest-run-cases">
                {[...latestCases]
                  .sort((a, b) => (a.status === 'failed' ? -1 : b.status === 'failed' ? 1 : 0))
                  .map((testCase, i) => (
                    <li key={i} className={`latest-run-case ${testCase.status}`}>
                      <span className="latest-run-case-mark">
                        {testCase.status === 'passed' ? '\u2713' : testCase.status === 'failed' ? '\u2717' : '\u2013'}
                      </span>
                      <span className="latest-run-case-name">{testCase.name}</span>
                      <span className="latest-run-case-status">
                        {testCase.status === 'not-run' ? 'did not run' : testCase.status}
                      </span>
                    </li>
                  ))}
              </ul>
            )}

            {latestRun.error_message && (
              <pre className="latest-run-error">{latestRun.error_message}</pre>
            )}
          </>
        )}
      </div>

      {/* Overview Metrics Cards */}
      <div className="overview-metrics">
        {overviewMetrics.map((metric, index) => (
          <div key={index} className="metric-card">
            <div className={`metric-icon ${metric.icon}`}>
              {metric.icon === 'blue' && (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                  <circle cx="12" cy="12" r="6" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                </svg>
              )}
              {metric.icon === 'green' && (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
              {metric.icon === 'red' && (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
              {metric.icon === 'purple' && (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                  <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              )}
            </div>
            <div className="metric-content">
              <div className="metric-value">{metric.value}</div>
              <div className="metric-title">{metric.title}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts Section */}
      <div className="charts-section">
{/* Pass/Fail Distribution Donut Chart */}
        <div className="chart-card">
          <h3 className="chart-title">Pass/Fail Distribution</h3>
          <div className="donut-chart-container">
            {loading ? (
              <div className="chart-shimmer" style={{ height: '300px', width: '100%', minHeight: '300px' }}>
                <div className="shimmer-chart-area" style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  flexDirection: 'column',
                  height: '300px',
                  width: '100%',
                  minHeight: '300px'
                }}>
                  {/* Simulate doughnut chart */}
                  <div style={{ 
                    width: '200px', 
                    height: '200px', 
                    minWidth: '200px',
                    minHeight: '200px',
                    borderRadius: '50%',
                    background: 'linear-gradient(90deg, #E5E7EB 0%, #F3F4F6 50%, #E5E7EB 100%)',
                    backgroundSize: '200% 100%',
                    animation: 'shimmer 1.5s infinite',
                    position: 'relative',
                    marginBottom: '1.5rem',
                    flexShrink: 0
                  }}>
                    <div className="shimmer-shine"></div>
                  </div>
                  {/* Simulate legend */}
                  <div className="shimmer-chart-legend" style={{ marginTop: 'auto' }}>
                    <div className="shimmer-line" style={{ width: '60px', height: '12px' }}></div>
                    <div className="shimmer-line" style={{ width: '60px', height: '12px' }}></div>
                  </div>
                  <div className="shimmer-shine"></div>
                </div>
              </div>
            ) : passFailData.passed + passFailData.failed > 0 ? (
              <>
                <div style={{ position: 'relative', width: '200px', height: '200px' }}>
                  <Doughnut
                    data={{
                      labels: ['Passed', 'Failed'],
                      datasets: [
                        {
                          data: [passFailData.passed, passFailData.failed],
                          backgroundColor: ['#10B981', '#EF4444'],
                          borderWidth: 0,
                          hoverOffset: 4
                        }
                      ]
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: true,
                      cutout: '70%',
                      plugins: {
                        legend: {
                          display: false
                        },
                        tooltip: {
                          backgroundColor: 'rgba(0, 0, 0, 0.8)',
                          padding: 12,
                          callbacks: {
                            label: function(context) {
                              const label = context.label || '';
                              const value = context.parsed || 0;
                              const total = passFailData.passed + passFailData.failed;
                              const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                              return `${label}: ${value} (${percentage}%)`;
                            }
                          }
                        }
                      }
                    }}
                  />
                  <div style={{ 
                    position: 'absolute', 
                    top: '50%', 
                    left: '50%', 
                    transform: 'translate(-50%, -50%)',
                    textAlign: 'center',
                    pointerEvents: 'none'
                  }}>
                    <div style={{ fontSize: '24px', fontWeight: '700', color: '#1F2937' }}>
                      {passFailData.passRate.toFixed(1)}%
                    </div>
                    <div style={{ fontSize: '12px', color: '#6B7280' }}>
                      Passed
                    </div>
                  </div>
                </div>
                <div className="donut-legend">
                  <div className="legend-item">
                    <div className="legend-color green"></div>
                    <span>Passed</span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-color red"></div>
                    <span>Failed</span>
                  </div>
                </div>
              </>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px', color: '#6B7280' }}>
                No test runs available
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recent runs, grouped by suite */}
      <div className="failing-tests-section">
        <h2 className="section-title">Recent runs</h2>
        <div className="recent-runs">
          {failingTestsLoading ? (
            <p className="recent-runs-empty">Loading recent runs&hellip;</p>
          ) : failingTests.length === 0 ? (
            <p className="recent-runs-empty">No runs in this period.</p>
          ) : (
            failingTests.map((suite) => (
              <div key={suite.key} className="recent-run-row">
                <div className="recent-run-main">
                  <span className="recent-run-name">{suite.name}</span>
                  <span className="recent-run-strip">
                    {suite.strip.map((status, i) => (
                      <span
                        key={i}
                        className={`recent-run-dot ${status}`}
                        title={status}
                      />
                    ))}
                  </span>
                </div>
                <div className="recent-run-meta">
                  <span className={`recent-run-rate ${suite.passRate === 100 ? 'good' : suite.passRate === 0 ? 'bad' : 'mixed'}`}>
                    {suite.passRate}% pass
                  </span>
                  <span className="recent-run-count">
                    {suite.passed}/{suite.total} run{suite.total === 1 ? '' : 's'}
                  </span>
                  <span className="recent-run-last">{suite.lastRun}</span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default Reports;
